import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import '../styles/Brand.css';
import { Container } from 'react-bootstrap';
import usePageNav from '../hooks/usePageNav';
import getNavigationInitialState from '../initialState/navigation';
import SearchPageProductList from './SearchPageProductList';
import PageNavCurPageIndicator from './PageNavCurPageIndicator';
import PageNav from './PageNav';
import { bool } from 'prop-types';
import useSearch from '../hooks/useSearch';
import constants from '../constants.json' assert { type: 'json' };
import Breadcrumbs from './Breadcrumbs';
import { ToastContext } from './context/ToastContext';
import SearchPageSidebarSkeleton from './SearchPageSidebarSkeleton';
import SearchPageSidebar from './SearchPageSidebar';
import PageSidebar from './PageSidebar';
import Seo from './Seo';
import hasStoreName from '../helpers/hasStoreName';
import AppContext from './context/AppContext';

const DEFAULT_LIMIT = 25;
const DEFAULT_SORT_BY = 'title_asc';

export default function Brand({ isMobile }) {
    const { brand } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { searchQuery, updateSearchParams } = useSearch();
    const [searchLoading, setSearchLoading] = useState(true);
    const [productList, setProductList] = useState([]);
    const { sendMessage } = useContext(ToastContext);
    const { appContext } = useContext(AppContext);
    const title = useMemo(() => {
        if (!brand) {
            return '';
        }
        let title = brand;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [brand]);
    /* Pagination code */
    const containerRef = useRef(null);
    const [pagesData, setPagesData] = useState(getNavigationInitialState());
    const handlePageChange = (newCurPage) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', parseInt(newCurPage));
        setSearchParams(newSearchParams);
    };
    const {
        curPage,
        curRange,
        nextRange,
        generatedPagesData,
        paginateUser,
    } = usePageNav({
        containerRef,
        pagesData,
        initialCurPage: Math.min(parseInt(searchParams.get('page') || 0, 0)),
        onUpdate: handlePageChange,
    });
    /* End pagination code */

    // Product fetching func.
    const handleProductsFetch = useCallback(async () => {
        try {
            setSearchLoading(true);
            const params = new URLSearchParams({
                ...{
                    vendor: brand,
                    fields: constants.MINIMAL_PRODUCT_FIELDS.join(','),
                    availableForSaleOnly: false,
                    limit: DEFAULT_LIMIT,
                    sortBy: DEFAULT_SORT_BY,
                },
                ...searchQuery,
            }).toString();
            // Products
            const productResponse = await fetch(new URL('api/products/filter?' + params, process.env.APP_URL));
            const productData = await productResponse.json();
            setProductList(productData.products ?? []);
            if (productData.pageInfo) {
                setPagesData({
                    limit: productData.pageInfo.limitPerPage,
                    count: productData.pageInfo.totalObjects,
                    pages: productData.pageInfo.totalPages,
                    availableTagPrefixes: productData.pageInfo.availableTagPrefixes ?? [],
                    availableTagFilters: productData.pageInfo.availableTagFilters ?? [],
                    availableVendors: productData.pageInfo.availableVendors ?? [],
                    productMaxPrice: productData.pageInfo.productMaxPrice,
                });
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setSearchLoading(false);
        }
    }, [searchQuery]);

    // Fetch products on load and update
    useEffect(() => {
        if (brand) {
            handleProductsFetch();
        }
    }, [brand, searchParams]);

    return (<Container
        className={'section'}
        style={{ padding: '0px 15px 30px' }}>
        <>
            <Seo
                seoTitle={title}
                canonicalUrl={new URL(`/brands/${brand}`, process.env.APP_URL).toString()}
                breadsArr={[
                    {
                        title: 'Home',
                        handle: '/',
                    },
                    {
                        title: 'Brands',
                        handle: '/brands',
                    },
                    {
                        title: brand,
                        handle: `/brands/${brand}`,
                    },
                ]}
            />
            <div
                className={'mt-2'}>
                <Breadcrumbs
                    breadcrumbList={[
                        ['Brands', '/brands'],
                        [brand, '/brands/' + brand]
                    ]} />
            </div>
            <div
                className={'brand-container'}>
                <div
                    className={'brand-sidebar--container'}>
                    <PageSidebar />
                </div>
                <div
                    className={'brand-product-list--container'}>
                    <h3
                        className={'m-0 p-0'}>{brand}</h3>
                    <div>
                        {(() => {
                            if (searchLoading) {
                                return <SearchPageSidebarSkeleton />;
                            }
                            if (productList.length > 0) {
                                return (
                                    <SearchPageSidebar
                                        useMinimalVersion={true}
                                        isMobile={isMobile}
                                        searchQuery={searchQuery}
                                        setSearchQuery={updateSearchParams}
                                        vendorList={pagesData.availableVendors ?? []}
                                        tagPrefixes={pagesData.availableTagPrefixes ?? []}
                                        tagList={pagesData.availableTagFilters ?? []}
                                        productMaxPrice={pagesData.productMaxPrice}
                                        searchLoading={searchLoading}
                                        productCount={productList.length}
                                    />
                                );
                            }
                            return null;
                        })()}
                        <SearchPageProductList
                            source={'Brand'}
                            ref={containerRef}
                            isMobile={isMobile}
                            productList={[...productList]}
                            searchLoading={searchLoading}
                            collectionHandle={''}
                            searchQuery={{
                                ...searchQuery,
                            }}
                            setProductList={setProductList}
                            totalPages={pagesData.pages}
                            curPage={curPage}
                            paginateUser={paginateUser}
                            setSearchQuery={updateSearchParams}
                            generatedPagesData={generatedPagesData}
                        >
                            {productList.length > 0 ? (
                                <>
                                    <PageNavCurPageIndicator
                                        curRange={curRange}
                                        nextRange={nextRange}
                                        totalCount={pagesData.count}
                                        resourceType={'products'}
                                        noStyle={true}
                                    />
                                    <PageNav
                                        curPage={curPage}
                                        totalPages={pagesData.pages}
                                        generatedPagesData={generatedPagesData}
                                        paginateUser={paginateUser}
                                    />
                                </>
                            ) : null}
                        </SearchPageProductList>
                    </div>
                </div>
            </div>
        </>
    </Container>);
}

Brand.propTypes = {
    isMobile: bool,
};
